import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getPhases,
  createPhase,
  updatePhase,
  deletePhase,
} from '../../../../../services/api';
import { COULEURS_PHASES } from '../utils/labelConstants';
import { mapApiSection } from '../utils/labelApiMapper';

const usePhaseCrud = (graphId, sensorIds, donnees, dateOptions) => {
  const [localPhases, setLocalPhases]     = useState([]);
  const [editingPhase, setEditingPhase]   = useState(null);
  const [creatingPhase, setCreatingPhase] = useState(null);
  const prochainIdPhase                   = useRef(0);

  // ── Chargement API ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!graphId || !sensorIds?.length) return;
    setLocalPhases([]);
    const promises = sensorIds.map((sensorId) =>
      getPhases({ sensorId })
        .then((res) => {
          const phases = res?.data?.data;
          if (Array.isArray(phases)) {
            const mapped = phases.map(mapApiSection);
            prochainIdPhase.current += mapped.length;
            setLocalPhases((prev) => [...prev, ...mapped]);
          }
        })
        .catch((err) => console.error('[usePhaseCrud] getPhases error:', err))
    );
    Promise.all(promises);
  }, [graphId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Créer une phase ───────────────────────────────────────────────────────────
  const ajouterPhase = useCallback(() => {
    const color = COULEURS_PHASES[prochainIdPhase.current % COULEURS_PHASES.length];
    const tArr = dateOptions.map((d) => d.ts);
    setCreatingPhase({
      id: null, tStart: tArr[0] || 0, tEnd: tArr[tArr.length - 1] || 0, label: '', color,
    });
  }, [dateOptions]);

  // ── Sauvegarder (création ou modification) ────────────────────────────────────
  const handleSavePhase = useCallback(
    async (draft) => {
      const tArr = donnees[0]?.x || donnees[0]?.t || [];
      const pkArr = donnees[0]?.pk || [];
      const findPk = (ts) => {
        let closest = 0, minDiff = Infinity;
        tArr.forEach((t, i) => { const d = Math.abs(t - ts); if (d < minDiff) { minDiff = d; closest = i; } });
        return pkArr[closest] ?? closest;
      };
      const payload = {
        graph_id: graphId,
        id_sensor: sensorIds[0] ?? null,
        pk_test_ref_start: findPk(draft.tStart),
        pk_test_ref_end: findPk(draft.tEnd),
        time_start: draft.tStart,
        time_end: draft.tEnd,
        phase_text: draft.label ?? '',
        color_hex: draft.color ?? '#888888',
      };
      const res = draft.id == null
        ? await createPhase(payload)
        : await updatePhase({ phaseId: draft.id, ...payload });
      const raw = res?.data?.data ?? res?.data;
      const saved = (raw?.time_start != null || raw?.t_start != null)
        ? raw
        : {
            id: raw?.id ?? draft.id, id_sensor: sensorIds[0] ?? null,
            time_start: draft.tStart, time_end: draft.tEnd,
            phase_text: draft.label ?? '', color_hex: draft.color ?? '#888888',
          };
      if (saved) {
        const mapped = mapApiSection(saved);
        setLocalPhases((prev) =>
          draft.id == null
            ? [...prev, mapped]
            : prev.map((p) => (p.id === draft.id ? mapped : p))
        );
        if (draft.id == null) prochainIdPhase.current += 1;
      }
      setCreatingPhase(null);
      setEditingPhase(null);
    },
    [graphId, sensorIds, donnees]
  );

  // ── Supprimer ─────────────────────────────────────────────────────────────────
  const handleDeletePhase = useCallback(async (draft) => {
    try {
      await deletePhase({ phaseId: draft.id });
      setLocalPhases((prev) => prev.filter((s) => s.id !== draft.id));
    } catch (err) {
      console.error('[usePhaseCrud] deletePhase error:', err);
    }
    setEditingPhase(null);
  }, []);

  // ── Ouvrir l'édition depuis un clic Plotly ────────────────────────────────────
  const handlePhaseClickInternal = useCallback(
    (phaseData) => {
      const found = localPhases.find(
        (s) => s.tStart === phaseData.tStart && s.tEnd === phaseData.tEnd
      );
      if (found) setEditingPhase({ ...found });
    },
    [localPhases]
  );

  return {
    localPhases,
    editingPhase, setEditingPhase,
    creatingPhase, setCreatingPhase,
    ajouterPhase,
    handleSavePhase,
    handleDeletePhase,
    handlePhaseClickInternal,
  };
};

export default usePhaseCrud;
